import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';

export interface ProductMediaVariantsDialogProps {
  /** How many files the choice applies to; null closes the dialog. */
  fileCount: number | null;
  /** The current scope when every target file shares it; empty means every variant. */
  initial: string[];
  /** The target files do not share one scope, so saving replaces each one's. */
  mixed: boolean;
  variants: ReadonlyArray<{ id: string; title: string }>;
  onClose: () => void;
  onSave: (variantIds: string[]) => void;
}

/** Which variants show the chosen files; nothing ticked means every variant. */
export function ProductMediaVariantsDialog({
  fileCount,
  initial,
  mixed,
  variants,
  onClose,
  onSave,
}: ProductMediaVariantsDialogProps) {
  const { t } = useTranslation(['products', 'common']);
  const [chosen, setChosen] = useState<string[]>(initial);
  const all = chosen.length === 0;

  return (
    <Dialog open={fileCount !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('products:media.variantsTitle', { count: fileCount ?? 1 })}</DialogTitle>
          <DialogDescription>
            {mixed ? t('products:media.variantsMixed') : t('products:media.variantsDescription')}
          </DialogDescription>
        </DialogHeader>
        {variants.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('products:media.scopeNoVariants')}</p>
        ) : (
          <div className="flex max-h-72 flex-col gap-2 overflow-y-auto py-1">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox checked={all} onCheckedChange={(v) => v === true && setChosen([])} />
              {t('products:media.scopeAll')}
            </label>
            {variants.map((variant) => (
              <label key={variant.id} className="flex items-center gap-2 pl-1 text-sm">
                <Checkbox
                  checked={chosen.includes(variant.id)}
                  onCheckedChange={(v) =>
                    setChosen((prev) =>
                      v === true ? [...prev, variant.id] : prev.filter((id) => id !== variant.id),
                    )
                  }
                />
                <span className="truncate">{variant.title}</span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={variants.length === 0}
            onClick={() => {
              onSave(chosen);
              onClose();
            }}
          >
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
